// M4.1 — spell catalogue.
//
// Pure data + helpers. Cast pipeline (channel, mana deduction, GCD, CD,
// resolution) lands in M4.2; this file only carries the templates and the
// `isSpellKnown` gate used by the spellbook UI.
//
// M7.2 made the runtime catalogue swappable via setSpellCatalogue, mirroring
// the items / mobs / quests / npcs pattern.

import { z } from "zod";
import type { CharacterStats } from "../types.js";

// =============================================================================
// Schools and effect taxonomy
// =============================================================================

// M8 — magic revamp: 6 schools. Stat gating still per-template (each spell
// declares its own `requirement.int/fai`); the school drives the matching
// resist on damage resolution + the visual hint.
export type SpellSchool =
  | "divine"
  | "necromancy"
  | "water"
  | "fire"
  | "air"
  | "earth";

export type SpellTargeting = "single" | "aoeGround" | "self";

// T4C-style cast tempo:
//   "auto" → entering the cast stance loops the spell on the locked mob target
//            until movement / death / mana-out / spell swap cancels it. Used
//            by single-target damage spells (Fireball / Frost Shock / Smite).
//   "once" → one-shot. Effect resolves after castTime, then per-spell cooldown
//            blocks the next press. Heals / buffs / AOE-ground spells.
export type SpellCastMode = "auto" | "once";

// Effect kinds the cast pipeline (M4.2+) will resolve. Listed here so the
// catalogue can be type-checked; the actual handlers come later.
export type SpellEffect =
  | { kind: "damage"; basePower: number }
  | { kind: "heal"; basePower: number }
  // DoT ticks every `tickIntervalMs` for `durationMs`, applying `tickPower`
  // (pre-scaling) per tick. Total = ceil(durationMs / tickIntervalMs) ticks.
  | { kind: "dot"; tickPower: number; tickIntervalMs: number; durationMs: number }
  // Slow as a movement-speed multiplier (0.5 = 50% speed). Negative debuff.
  | { kind: "slow"; speedMult: number; durationMs: number }
  // Self-buff that absorbs incoming damage before HP. Pool = base + scale*INT
  // when cast. Expires by duration OR pool exhaustion.
  | { kind: "absorb"; basePool: number; intScale: number; durationMs: number }
  // Outgoing damage multiplier buff on an ally (or self).
  | { kind: "buffDmg"; dmgPct: number; durationMs: number };

export interface SpellRequirement {
  int?: number;
  fai?: number;
}

export interface SpellTemplate {
  id: string;
  name: string;
  school: SpellSchool;
  castMode: SpellCastMode;
  requirement: SpellRequirement;
  manaCost: number;
  castTimeMs: number;
  cooldownMs: number;
  range: number; // tiles (Chebyshev); 0 = self
  targeting: SpellTargeting;
  aoeRadius?: number; // only for targeting === "aoeGround"
  effects: SpellEffect[];
  // Display hint for spell-effect circles (M4.5). Not enforced by logic.
  visualHint: "fire" | "frost" | "arcane" | "divine";
}

// =============================================================================
// 8-spell starter catalogue (per PROMPT.md §5.4)
// =============================================================================

export const BUILTIN_SPELL_CATALOGUE: readonly SpellTemplate[] = [
  {
    id: "fireball",
    name: "Fireball",
    school: "fire",
    castMode: "auto",
    requirement: { int: 10 },
    manaCost: 10,
    // Auto damage spells: instant cast (no wind-up bar). The cooldown alone
    // paces the loop, scaled by Rapidité.
    castTimeMs: 0,
    cooldownMs: 1500,
    range: 16,
    targeting: "single",
    effects: [{ kind: "damage", basePower: 20 }],
    visualHint: "fire",
  },
  {
    id: "frost_shock",
    name: "Frost Shock",
    school: "water",
    castMode: "auto",
    requirement: { int: 15 },
    manaCost: 15,
    castTimeMs: 0,
    cooldownMs: 4000,
    range: 14,
    targeting: "single",
    effects: [
      { kind: "damage", basePower: 18 },
      { kind: "slow", speedMult: 0.5, durationMs: 3000 },
    ],
    visualHint: "frost",
  },
  {
    id: "arcane_shield",
    name: "Arcane Shield",
    school: "air",
    castMode: "once",
    requirement: { int: 20 },
    manaCost: 25,
    castTimeMs: 0,
    cooldownMs: 30_000,
    range: 0,
    targeting: "self",
    effects: [{ kind: "absorb", basePool: 50, intScale: 5, durationMs: 10_000 }],
    visualHint: "arcane",
  },
  {
    id: "rain_of_fire",
    name: "Rain of Fire",
    school: "fire",
    castMode: "once",
    requirement: { int: 25 },
    manaCost: 40,
    castTimeMs: 1500,
    cooldownMs: 12_000,
    range: 16,
    targeting: "aoeGround",
    aoeRadius: 3,
    effects: [{ kind: "dot", tickPower: 10, tickIntervalMs: 1000, durationMs: 3000 }],
    visualHint: "fire",
  },
  {
    id: "light_heal",
    name: "Light Heal",
    school: "divine",
    castMode: "once",
    requirement: { fai: 10 },
    manaCost: 10,
    castTimeMs: 0,
    cooldownMs: 1500,
    range: 12,
    targeting: "single",
    effects: [{ kind: "heal", basePower: 25 }],
    visualHint: "divine",
  },
  {
    id: "blessing",
    name: "Blessing",
    school: "divine",
    castMode: "once",
    requirement: { fai: 15 },
    manaCost: 20,
    castTimeMs: 1000,
    cooldownMs: 10_000,
    range: 12,
    targeting: "single",
    effects: [{ kind: "buffDmg", dmgPct: 0.2, durationMs: 30_000 }],
    visualHint: "divine",
  },
  {
    id: "smite",
    name: "Smite",
    school: "divine",
    castMode: "auto",
    requirement: { fai: 20 },
    manaCost: 20,
    castTimeMs: 0,
    cooldownMs: 2000,
    range: 16,
    targeting: "single",
    effects: [{ kind: "damage", basePower: 22 }],
    visualHint: "divine",
  },
  {
    id: "group_heal",
    name: "Group Heal",
    school: "divine",
    castMode: "once",
    requirement: { fai: 25 },
    manaCost: 40,
    castTimeMs: 1500,
    cooldownMs: 15_000,
    range: 0,
    targeting: "aoeGround",
    aoeRadius: 4,
    effects: [{ kind: "heal", basePower: 30 }],
    visualHint: "divine",
  },
];

// Back-compat alias.
export const SPELL_CATALOGUE = BUILTIN_SPELL_CATALOGUE;

// =============================================================================
// Runtime registry (M7.2)
// =============================================================================

let runtimeSpellList: SpellTemplate[] = [...BUILTIN_SPELL_CATALOGUE];
let runtimeSpellById: Map<string, SpellTemplate> = new Map(
  runtimeSpellList.map((s) => [s.id, s]),
);

export function getSpellTemplate(id: string): SpellTemplate | null {
  return runtimeSpellById.get(id) ?? null;
}

export function getSpellCatalogue(): readonly SpellTemplate[] {
  return runtimeSpellList;
}

export function setSpellCatalogue(spells: readonly SpellTemplate[]): void {
  runtimeSpellList = [...spells];
  runtimeSpellById = new Map(runtimeSpellList.map((s) => [s.id, s]));
}

export function _resetSpellCatalogueForTests(): void {
  runtimeSpellList = [...BUILTIN_SPELL_CATALOGUE];
  runtimeSpellById = new Map(runtimeSpellList.map((s) => [s.id, s]));
}

// =============================================================================
// Helpers
// =============================================================================

export function isAutoCastSpell(spell: SpellTemplate): boolean {
  return spell.castMode === "auto";
}

// T4C-style cast tempo: total cycle (castTime + cooldown) scaled by Rapidité,
// clamped to a minimum so high-RAP doesn't trivialise the spell. Split keeps
// the ratio between castTime and cooldown so the visual cast bar stays
// proportional. For instant spells (castTime=0), castTimeScaled stays 0.
export const MIN_SPELL_CYCLE_MS = 500;

export interface SpellCycleTimings {
  castTimeMs: number;
  cooldownMs: number;
  totalMs: number;
}

export function castCycleTimings(spell: SpellTemplate, rap: number): SpellCycleTimings {
  const rapMult = 1 + Math.max(0, rap) * 0.01;
  const baseTotal = spell.castTimeMs + spell.cooldownMs;
  const scaledTotal = Math.max(MIN_SPELL_CYCLE_MS, baseTotal / rapMult);
  if (baseTotal === 0) {
    return { castTimeMs: 0, cooldownMs: scaledTotal, totalMs: scaledTotal };
  }
  const castTimeMs = scaledTotal * (spell.castTimeMs / baseTotal);
  const cooldownMs = scaledTotal - castTimeMs;
  return { castTimeMs, cooldownMs, totalMs: scaledTotal };
}

// Equip-style rule (gated by BASE stats, not effective) so affixes don't
// bootstrap into spell access — symmetric with item requirements (PROMPT.md
// §5.2). Pass the player's base stats here, not effective.
export function isSpellKnown(spell: SpellTemplate, baseStats: CharacterStats): boolean {
  if (spell.requirement.int !== undefined && baseStats.int < spell.requirement.int) return false;
  if (spell.requirement.fai !== undefined && baseStats.fai < spell.requirement.fai) return false;
  return true;
}

export function knownSpellIds(baseStats: CharacterStats): string[] {
  return runtimeSpellList
    .filter((s) => isSpellKnown(s, baseStats))
    .map((s) => s.id);
}

// =============================================================================
// Runtime buff snapshot (wire shape)
// =============================================================================

// Server emits a buffUpdate message with this shape per buffed player. The
// list reflects the player's currently-active buffs at the moment of the
// broadcast; the client replaces its local cache on every update.
export type BuffSnapshotKind = "buffDmg" | "absorb";

export interface BuffSnapshot {
  spellId: string;
  kind: BuffSnapshotKind;
  // Absolute serverTime when this buff falls off.
  expiresAt: number;
  // Filled when kind === "buffDmg" — additive damage multiplier (0.2 = +20%).
  dmgPct?: number;
  // Filled when kind === "absorb" — remaining shield HP + initial value for
  // the client-side progress bar.
  absorbRemaining?: number;
  absorbInitial?: number;
}

export const HOTBAR_SIZE = 8;
export type HotbarSlots = (string | null)[];

export function emptyHotbar(): HotbarSlots {
  return new Array<string | null>(HOTBAR_SIZE).fill(null);
}

// =============================================================================
// Zod schemas (M7.2)
// =============================================================================

const SPELL_ID_REGEX = /^[a-z][a-z0-9_]{2,40}$/;

const SpellEffectSchema = z.discriminatedUnion("kind", [
  z
    .object({ kind: z.literal("damage"), basePower: z.number().min(0).max(100_000) })
    .strict(),
  z
    .object({ kind: z.literal("heal"), basePower: z.number().min(0).max(100_000) })
    .strict(),
  z
    .object({
      kind: z.literal("dot"),
      tickPower: z.number().min(0).max(100_000),
      tickIntervalMs: z.number().int().min(100).max(60_000),
      durationMs: z.number().int().min(100).max(300_000),
    })
    .strict(),
  z
    .object({
      kind: z.literal("slow"),
      speedMult: z.number().min(0).max(1),
      durationMs: z.number().int().min(100).max(300_000),
    })
    .strict(),
  z
    .object({
      kind: z.literal("absorb"),
      basePool: z.number().min(0).max(100_000),
      intScale: z.number().min(0).max(1_000),
      durationMs: z.number().int().min(100).max(300_000),
    })
    .strict(),
  z
    .object({
      kind: z.literal("buffDmg"),
      dmgPct: z.number().min(-1).max(10),
      durationMs: z.number().int().min(100).max(300_000),
    })
    .strict(),
]);

export const SpellTemplateSchema = z
  .object({
    id: z.string().regex(SPELL_ID_REGEX),
    name: z.string().min(1).max(60),
    school: z.enum(["divine", "necromancy", "water", "fire", "air", "earth"]),
    castMode: z.enum(["auto", "once"]),
    requirement: z
      .object({
        int: z.number().int().min(0).max(1_000).optional(),
        fai: z.number().int().min(0).max(1_000).optional(),
      })
      .strict(),
    manaCost: z.number().int().min(0).max(10_000),
    castTimeMs: z.number().int().min(0).max(60_000),
    cooldownMs: z.number().int().min(0).max(600_000),
    range: z.number().int().min(0).max(60),
    targeting: z.enum(["single", "aoeGround", "self"]),
    aoeRadius: z.number().int().min(0).max(30).optional(),
    effects: z.array(SpellEffectSchema).min(1).max(5),
    visualHint: z.enum(["fire", "frost", "arcane", "divine"]),
  })
  .strict();

export const SpellCatalogueSchema = z.array(SpellTemplateSchema);

// Strips any stale hotbar entries that reference a spell the player no longer
// knows (e.g. respec) or that point at an unknown id. Used on snapshot + on
// every hotbar mutation to keep the wire payload clean.
export function sanitizeHotbar(hotbar: HotbarSlots, baseStats: CharacterStats): HotbarSlots {
  const cleaned: HotbarSlots = new Array<string | null>(HOTBAR_SIZE).fill(null);
  for (let i = 0; i < HOTBAR_SIZE && i < hotbar.length; i++) {
    const id = hotbar[i];
    if (id === null || id === undefined) continue;
    const tpl = getSpellTemplate(id);
    if (!tpl) continue;
    if (!isSpellKnown(tpl, baseStats)) continue;
    cleaned[i] = id;
  }
  return cleaned;
}
